import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { ButtonLink as SparkyButtonLink } from '@eneco/sparky';
import type { ButtonLinkProps } from '@eneco/sparky/types';

import type { ButtonLinkContent } from './button-link-content.types';

/**
 * `ButtonLink` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ButtonLink/ButtonLink.tsx`: a single Sparky
 * `ButtonLink` whose label, destination and target all come from the one `buttonLink` field, coloured
 * by the `buttonColor` rendering parameter.
 *
 * `buttonColor` normalisation is carried over verbatim from the legacy component — `'primary'` passes
 * through and anything else is lowercased — rather than being tightened to the two values the Sitecore
 * parameters template actually offers (`libs/sitecore/types/manual/ButtonLink.d.ts`), so a stray
 * casing in content behaves identically on both lanes.
 *
 * Drops the legacy `useLinkComponent()` in favour of `adapter.toLink()` plus Sparky's own `href`,
 * matching every other migrated component in this lane (see `LinkCard.tsx`, `HeroCard.tsx`).
 *
 * Known simplification: `buttonLink.reference` is an unresolved `migrationUnresolvedReference` stub
 * (ADR-0049), so on the Sanity lane an *internal* link has no slug to resolve to and `toLink` returns
 * `undefined`. Since the link is this block's entire payload, the block then renders nothing rather
 * than a button pointing at the wrong place — the same trade `mappers.ts#toLink` makes everywhere.
 * External links, which carry a real `href`, are unaffected, and the Sitecore lane resolves both.
 */
const normalizeButtonColor = (color: string): ButtonLinkProps['action'] =>
  color === 'primary' ? 'primary' : (color.toLowerCase() as ButtonLinkProps['action']);

const ButtonLink: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ButtonLinkContent>();
  const buttonColor = useSetting('buttonColor');

  const link = adapter.toLink(content.buttonLink);
  if (!link?.href) return null;

  const action = buttonColor ? normalizeButtonColor(buttonColor) : undefined;

  return (
    <SparkyButtonLink href={link.href} action={action} target={link.target}>
      {link.text}
    </SparkyButtonLink>
  );
};

export default ButtonLink;
